import React, { useState, useEffect } from 'react';
import CampaignPlanner from './CampaignPlanner';
import { fetchCampaigns, deleteCampaign, fetchSocialConnections, saveSocialConnection, removeSocialConnection } from '../services/campaignService';

const PLATFORMS = [
  {
    id: 'tiktok',
    name: 'TikTok',
    color: '#010101',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/>
      </svg>
    ),
  },
  {
    id: 'instagram',
    name: 'Instagram',
    color: '#E1306C',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
  },
  {
    id: 'meta',
    name: 'Meta (Facebook)',
    color: '#1877F2',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
];

// Generates the OAuth URL for a given platform
function getOAuthUrl(platformId) {
  const origin = window.location.origin;
  const redirectUri = encodeURIComponent(`${origin}/oauth-callback`);

  if (platformId === 'tiktok') {
    const clientKey = process.env.REACT_APP_TIKTOK_CLIENT_KEY || 'YOUR_TIKTOK_CLIENT_KEY';
    return `https://www.tiktok.com/v2/auth/authorize?client_key=${clientKey}&response_type=code&scope=user.info.basic,video.publish&redirect_uri=${redirectUri}`;
  }
  if (platformId === 'instagram' || platformId === 'meta') {
    const appId = process.env.REACT_APP_META_APP_ID || 'YOUR_META_APP_ID';
    const scope = 'pages_manage_posts,instagram_basic,instagram_content_publish,public_profile';
    return `https://www.facebook.com/v19.0/dialog/oauth?client_id=${appId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code`;
  }
  return null;
}

function SocialConnectionCard({ platform, connection, onConnect, onDisconnect }) {
  const isConnected = !!connection;
  return (
    <div className="flex items-center justify-between p-4 border border-gray-100 rounded-2xl bg-white">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: platform.color + '15', color: platform.color }}>
          {platform.icon}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{platform.name}</p>
          {isConnected
            ? <p className="text-xs text-emerald-600">Verbunden</p>
            : <p className="text-xs text-gray-400">Nicht verbunden</p>
          }
        </div>
      </div>
      {isConnected ? (
        <button
          onClick={() => onDisconnect(platform.id)}
          className="text-xs text-red-500 hover:text-red-700 border border-red-100 hover:border-red-200 px-3 py-1.5 rounded-lg transition-all"
        >
          Trennen
        </button>
      ) : (
        <button
          onClick={() => onConnect(platform.id)}
          className="text-xs text-white font-medium px-3 py-1.5 rounded-lg transition-all"
          style={{ backgroundColor: platform.color }}
        >
          Verbinden
        </button>
      )}
    </div>
  );
}

function CampaignCard({ campaign, onOpen, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  const GOAL_LABELS = {
    brand_awareness: 'Markenbekanntheit',
    lead_gen:        'Lead-Generierung',
    sales:           'Verkäufe',
    engagement:      'Engagement',
  };

  return (
    <div
      className="group bg-white border border-gray-100 hover:border-indigo-200 hover:shadow-md rounded-2xl p-5 cursor-pointer transition-all relative"
      onClick={() => onOpen(campaign.id)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors truncate">{campaign.title}</h3>
          <p className="text-xs text-gray-400 mt-0.5">{GOAL_LABELS[campaign.goal] || campaign.goal}</p>
        </div>
        <span className="shrink-0 text-xs bg-indigo-50 text-indigo-600 font-medium px-2 py-0.5 rounded-full ml-2">
          {campaign.status === 'draft' ? 'Entwurf' : campaign.status === 'active' ? 'Aktiv' : 'Abgeschlossen'}
        </span>
      </div>
      <div className="flex gap-4 text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/><path d="M5 7h4M7 5v4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/></svg>
          {Number(campaign.budget).toLocaleString('de-DE')} €
        </span>
        <span className="flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><rect x="1" y="2" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.2"/><path d="M4 1v2M10 1v2M1 6h12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
          {campaign.duration} Tage
        </span>
      </div>
      {confirming ? (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-white rounded-lg shadow border border-red-100 px-2 py-1" onClick={e => e.stopPropagation()}>
          <span className="text-xs text-red-600 font-medium">Löschen?</span>
          <button className="text-xs text-red-600 font-semibold hover:text-red-700" onClick={e => { e.stopPropagation(); onDelete(campaign.id); }}>Ja</button>
          <button className="text-xs text-gray-400" onClick={e => { e.stopPropagation(); setConfirming(false); }}>Nein</button>
        </div>
      ) : (
        <button
          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 w-7 h-7 bg-white/90 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 shadow-sm transition-all"
          onClick={e => { e.stopPropagation(); setConfirming(true); }}
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 3.5H12M5 3.5V2.5H9V3.5M5.5 6V10.5M8.5 6V10.5M3 3.5L3.5 11.5H10.5L11 3.5H3Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      )}
    </div>
  );
}

export default function AgencyTools({ user, onBack }) {
  const [view, setView]                   = useState('hub'); // 'hub' | 'planner' | 'campaign-detail'
  const [campaigns, setCampaigns]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [connections, setConnections]     = useState({});
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [activeTab, setActiveTab]         = useState('campaigns'); // 'campaigns' | 'social'

  useEffect(() => {
    Promise.all([
      fetchCampaigns(user.uid),
      fetchSocialConnections(user.uid),
    ]).then(([camps, conns]) => {
      setCampaigns(camps.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
      setConnections(conns);
    }).finally(() => setLoading(false));
  }, [user.uid]);

  const handleConnect = (platformId) => {
    const url = getOAuthUrl(platformId);
    if (url) {
      // Store pending connection info so the OAuth callback can identify it
      sessionStorage.setItem('pending_oauth_platform', platformId);
      sessionStorage.setItem('pending_oauth_uid', user.uid);
      window.open(url, '_blank', 'width=600,height=700');
    }
  };

  const handleDisconnect = async (platformId) => {
    await removeSocialConnection(user.uid, platformId);
    setConnections(c => { const next = { ...c }; delete next[platformId]; return next; });
  };

  const handleDeleteCampaign = async (id) => {
    await deleteCampaign(id);
    setCampaigns(cs => cs.filter(c => c.id !== id));
  };

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'OAUTH_SUCCESS') {
        const { platform, accessToken, expiresAt } = e.data;
        saveSocialConnection(user.uid, platform, { accessToken, expiresAt }).then(() => {
          setConnections(c => ({ ...c, [platform]: { accessToken, expiresAt } }));
        });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [user.uid]);

  if (view === 'planner') {
    return (
      <CampaignPlanner
        user={user}
        onClose={() => {
          fetchCampaigns(user.uid).then(camps => setCampaigns(camps.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0))));
          setView('hub');
        }}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 6H2M5 3L2 6L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Dashboard
          </button>
          <span className="text-gray-200">/</span>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Agentur-Tools</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-purple-100 text-purple-700 font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1l1.5 3H11l-2.5 2 1 3L6 7.5 2.5 9l1-3L1 4h3.5L6 1z" fill="currentColor"/></svg>
            Agency Plan
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${activeTab === 'campaigns' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Kampagnen
        </button>
        <button
          onClick={() => setActiveTab('social')}
          className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${activeTab === 'social' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Social Media
        </button>
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-gray-500">{campaigns.length} Kampagne{campaigns.length !== 1 ? 'n' : ''}</p>
            <button
              onClick={() => setView('planner')}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-xl transition-all shadow-sm"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M6 1V11M1 6H11" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              Neue Kampagne
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <svg className="animate-spin w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-14 h-14 bg-indigo-50 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <p className="text-gray-400 text-sm">Noch keine Kampagnen</p>
              <button onClick={() => setView('planner')} className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                Erste Kampagne planen →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map(c => (
                <CampaignCard
                  key={c.id}
                  campaign={c}
                  onOpen={() => { setSelectedCampaignId(c.id); setView('planner'); }}
                  onDelete={handleDeleteCampaign}
                />
              ))}
              <button
                onClick={() => setView('planner')}
                className="min-h-[120px] rounded-2xl border-2 border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30 flex flex-col items-center justify-center gap-2 text-gray-300 hover:text-indigo-400 transition-all"
              >
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 4V18M4 11H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                <span className="text-xs font-medium">Neue Kampagne</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Social Media Tab */}
      {activeTab === 'social' && (
        <div>
          <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-2xl">
            <p className="text-xs text-amber-700 font-medium mb-1">Hinweis zur Einrichtung</p>
            <p className="text-xs text-amber-600 leading-relaxed">
              Um Social-Media-Konten zu verbinden, müssen die OAuth-App-IDs in den Umgebungsvariablen konfiguriert sein:
              <code className="bg-amber-100 px-1 rounded ml-1">REACT_APP_LINKEDIN_CLIENT_ID</code> und
              <code className="bg-amber-100 px-1 rounded ml-1">REACT_APP_META_APP_ID</code>.
            </p>
          </div>
          <div className="space-y-3">
            {PLATFORMS.map(platform => (
              <SocialConnectionCard
                key={platform.id}
                platform={platform}
                connection={connections[platform.id]}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-5 text-center">
            Verbundene Konten werden sicher gespeichert. Tokens werden niemals im Klartext weitergegeben.
          </p>
        </div>
      )}
    </div>
  );
}
